import { modelData } from 'app/lib/modelData'
import { toCallAiInput } from 'app/lib/mutationInputs'
import { withZod } from '@remix-validated-form/with-zod'
import { Plus, Trash } from 'iconoir-react'
import { useMemo, useState } from 'react'
import { z } from 'zod'
import Button from 'app/components/Button'
import { Checkbox } from 'app/components/ui/checkbox'
import { SmartForm } from 'app/components/form/SmartForm'
import ComboMultiSelect from 'app/components/ui/combo/combo-multi-select'
import type { CallJson, PersonSummary } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useCallStore } from 'app/stores/StoreContext'
import { callOutcomes } from 'types/CallDataExtraction'
import { logError } from 'lib/logging'
import type { OptionType } from 'types/OptionType'

const aiSchema = z.object({})

export const aiValidator = withZod(aiSchema)

type Props = {
  call: CallJson
  onUpdated?: (call: CallJson) => void
}

const outcomeOptions: OptionType[] = Object.keys(callOutcomes).map((key) => ({
  id: key,
  name: key,
  value: key,
}))

export function EditAIDataModal({ call, onUpdated }: Props) {
  const [people, setPeople] = useState<PersonSummary[]>(call.ai?.people || [])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const callStore = useCallStore()

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      if (!call.id) throw new Error('Call ID is required')
      const result = await callStore.update(call.id, { ai: toCallAiInput({ ...call.ai, people }) })
      setIsSubmitting(false)
      if (result.error) {
        notifyError(result.error.message || `Failed to update AI data`)
      } else {
        notifySuccess(`AI data updated`)
        const updated = modelData(result.data?.updateCall)
        if (updated) onUpdated?.(updated)
        closeModal()
      }
    } catch (err) {
      setIsSubmitting(false)
      logError(`Failed to update AI data`, err)
      notifyError(`Failed to update AI data`)
    }
  }

  const updatePerson = (index: number, field: keyof PersonSummary, value: any) => {
    setPeople((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)))
  }

  const removePerson = (index: number) => {
    setPeople((prev) => prev.filter((_, i) => i !== index))
  }

  const addPerson = () => {
    setPeople((prev) => [...prev, { name: ``, patientType: `Other`, isCaller: false }])
  }

  return (
    <div className="flex max-h-[80vh] w-[700px] max-w-[90vw] flex-col">
      <SmartForm
        id="edit-ai-form"
        className="!mt-0 gap-y-4 px-6 pb-4"
        validator={aiValidator}
        onSubmit={handleSubmit}
        onCancel={closeModal}
        hideButtons
        preventSubmit
      >
        <div className="col-span-6 max-h-[60vh] overflow-y-auto">
          {call.ai?.summary && (
            <div className="mb-4">
              <label className="mb-1 block text-ui-sm font-medium text-strong">Summary</label>
              <div className="rounded border border-default bg-surface-subtle px-3 py-2 text-ui-sm text-default">
                {call.ai.summary}
              </div>
            </div>
          )}

          <div className="mb-2 flex items-center justify-between">
            <span className="text-ui-sm font-medium text-strong">People</span>
            <Button type="button" mode="white" size="sm" onClick={addPerson} className="!h-7 !px-2">
              <Plus width={14} height={14} />
              <span className="ml-1">Add Person</span>
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            {people.map((person, idx) => (
              <div key={idx} className="rounded-lg border border-default bg-surface-subtle">
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-ui-sm font-medium">
                    {person.name || `Person ${idx + 1}`}
                    {person.isCaller && <span className="ml-2 text-subtle">(Caller)</span>}
                  </span>
                  <button
                    type="button"
                    onClick={() => removePerson(idx)}
                    className="rounded p-1 text-subtle hover:bg-surface-strong hover:text-red-600"
                  >
                    <Trash width={14} height={14} />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3 border-t border-default bg-surface p-3">
                  <div className="col-span-1">
                    <label className="mb-1 block text-ui-xs text-subtle">Name</label>
                    <input
                      type="text"
                      value={person.name || ``}
                      onChange={(e) => updatePerson(idx, `name`, e.target.value)}
                      className="w-full rounded border border-default px-2 py-1.5 text-ui-sm"
                      placeholder="Name"
                    />
                  </div>

                  <div className="col-span-1">
                    <label className="mb-1 block text-ui-xs text-subtle">Phone</label>
                    <input
                      type="text"
                      value={person.phone || ``}
                      onChange={(e) => updatePerson(idx, `phone`, e.target.value)}
                      className="w-full rounded border border-default px-2 py-1.5 text-ui-sm"
                      placeholder="Phone"
                    />
                  </div>

                  <div className="col-span-1">
                    <label className="mb-1 block text-ui-xs text-subtle">Email</label>
                    <input
                      type="text"
                      value={person.email || ``}
                      onChange={(e) => updatePerson(idx, `email`, e.target.value)}
                      className="w-full rounded border border-default px-2 py-1.5 text-ui-sm"
                      placeholder="Email"
                    />
                  </div>

                  <div className="col-span-1">
                    <label className="mb-1 block text-ui-xs text-subtle">Birth Date</label>
                    <input
                      type="date"
                      value={person.birthDate || ``}
                      onChange={(e) => updatePerson(idx, `birthDate`, e.target.value)}
                      className="w-full rounded border border-default px-2 py-1.5 text-ui-sm"
                    />
                  </div>

                  <div className="col-span-1">
                    <label className="mb-1 block text-ui-xs text-subtle">Patient Type</label>
                    <select
                      value={person.patientType || `Other`}
                      onChange={(e) => updatePerson(idx, `patientType`, e.target.value)}
                      className="w-full rounded border border-default px-2 py-1.5 text-ui-sm"
                    >
                      <option value="New Patient">New Patient</option>
                      <option value="Current Patient">Current Patient</option>
                      <option value="Non-Patient">Non-Patient</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="col-span-1">
                    <label className="mb-1 block text-ui-xs text-subtle">Relationship</label>
                    <input
                      type="text"
                      value={person.relationship || ``}
                      onChange={(e) => updatePerson(idx, `relationship`, e.target.value)}
                      className="w-full rounded border border-default px-2 py-1.5 text-ui-sm"
                      placeholder="e.g., spouse, parent"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="mb-1 block text-ui-xs text-subtle">Treatment / Service Requested</label>
                    <input
                      type="text"
                      value={person.treatment || person.serviceRequested || ``}
                      onChange={(e) => updatePerson(idx, `treatment`, e.target.value)}
                      className="w-full rounded border border-default px-2 py-1.5 text-ui-sm"
                      placeholder="Treatment requested"
                    />
                  </div>

                  <div className="col-span-1">
                    <label className="mb-1 block text-ui-xs text-subtle">Appointment Date</label>
                    <input
                      type="date"
                      value={person.appointmentDate || ``}
                      onChange={(e) => updatePerson(idx, `appointmentDate`, e.target.value)}
                      className="w-full rounded border border-default px-2 py-1.5 text-ui-sm"
                    />
                  </div>

                  <div className="col-span-1">
                    <label className="mb-1 block text-ui-xs text-subtle">Appointment Time</label>
                    <input
                      type="time"
                      value={person.appointmentTime || ``}
                      onChange={(e) => updatePerson(idx, `appointmentTime`, e.target.value)}
                      className="w-full rounded border border-default px-2 py-1.5 text-ui-sm"
                    />
                  </div>

                  <div className="col-span-1">
                    <label className="mb-1 block text-ui-xs text-subtle">Insurance Provider</label>
                    <input
                      type="text"
                      value={person.insuranceProvider || ``}
                      onChange={(e) => updatePerson(idx, `insuranceProvider`, e.target.value)}
                      className="w-full rounded border border-default px-2 py-1.5 text-ui-sm"
                      placeholder="Insurance provider"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="mb-1 block text-ui-xs text-subtle">Outcomes</label>
                    <OutcomesSelect
                      values={person.outcomes as string[] | undefined}
                      onChange={(vals) => updatePerson(idx, `outcomes`, vals)}
                    />
                  </div>

                  <div className="col-span-1">
                    <label className="mb-1 block text-ui-xs text-subtle">Reason Not Scheduled</label>
                    <input
                      type="text"
                      value={person.reasonForNotScheduling || ``}
                      onChange={(e) => updatePerson(idx, `reasonForNotScheduling`, e.target.value)}
                      className="w-full rounded border border-default px-2 py-1.5 text-ui-sm"
                      placeholder="e.g., too expensive, no openings"
                    />
                  </div>

                  <div className="col-span-1 flex items-end gap-2 pb-1.5">
                    <label className="flex items-center gap-2 text-ui-xs text-[10px]!">
                      <Checkbox
                        checked={person.isCaller || false}
                        onCheckedChange={(v) => updatePerson(idx, `isCaller`, v === true)}
                      />
                      Is Caller
                    </label>

                    <label className="flex items-center gap-2 text-ui-xs text-[10px]!">
                      <Checkbox
                        checked={person.appointmentScheduled || false}
                        onCheckedChange={(v) => updatePerson(idx, `appointmentScheduled`, v === true)}
                      />
                      Apt. Scheduled
                    </label>

                    <label className="flex items-center gap-2 text-ui-xs text-[10px]!">
                      <Checkbox
                        checked={person.appointmentRequested || false}
                        onCheckedChange={(v) => updatePerson(idx, `appointmentRequested`, v === true)}
                      />
                      Apt. Requested
                    </label>
                  </div>
                </div>
              </div>
            ))}

            {people.length === 0 && (
              <div className="rounded-lg border border-dashed border-strong bg-surface-subtle p-4 text-center text-ui-sm text-subtle">
                No people identified. Click "Add Person" to add one.
              </div>
            )}
          </div>
        </div>

        <div className="col-span-6 flex justify-end gap-3 border-t border-default pt-4">
          <Button type="button" mode="white" size="md" onClick={closeModal}>
            Cancel
          </Button>
          <Button type="submit" mode="primary" size="md" loading={isSubmitting}>
            Save Changes
          </Button>
        </div>
      </SmartForm>
    </div>
  )
}

function OutcomesSelect({ values, onChange }: { values: string[] | undefined; onChange: (vals: string[]) => void }) {
  const selected = useMemo(
    () => (values || []).map((v) => outcomeOptions.find((o) => o.id === v) || { id: v, name: v, value: v }),
    [values],
  )

  return (
    <ComboMultiSelect
      name="outcomes"
      options={outcomeOptions}
      values={selected}
      placeholder="Select outcomes…"
      onChange={(opts) => onChange(opts.map((o) => o.value || o.id))}
    />
  )
}
